/**
 * Origin Cell Renderer for Assets Table
 *
 * Thin wrapper around shared OriginCell with Assets-specific features:
 * - Filter-aware ordering driven by the active table origin filters
 * - Fallback to legacy source field for backward compatibility
 * - Automatic optimization via React Compiler
 *
 * Shared features (via components/cells/OriginCell):
 * - Chariot rollup pattern for native capabilities
 * - Canonical origin ordering (matched-filter tier first, 'Praetorian' promoted)
 * - ChipsList display with "+N" counter
 * - Tooltip showing all origins
 * - Automatic deduplication
 */

import React from 'react';

import { OriginCell as SharedOriginCell } from '@/components/table/columns/cells/OriginCell';
import type { Asset } from '@/types';

import { useCellFilters } from './CellFiltersContext';

interface AssetOriginCellProps {
  asset: Asset;
}

/**
 * Assets table origin cell renderer.
 *
 * Reads the active origin filters from the cell filter context and forwards
 * them to the shared OriginCell, which handles rollup + canonical ordering
 * (filter-matched origins first, then alphabetical with 'Praetorian' promoted).
 *
 * React Compiler automatically optimizes this component's re-renders.
 *
 * @param asset - The asset to render origins for
 * @returns JSX element with ordered origin chips or dash if no origins
 */
export const OriginCell: React.FC<AssetOriginCellProps> = ({ asset }) => {
  'use memo';

  // Access filter context for filter-aware ordering
  const filters = useCellFilters();
  const activeOriginFilters = filters?.advancedFilters?.origins;

  // Extract and deduplicate raw sources (with legacy source fallback)
  const rawSources = asset.origins || (asset.source ? [asset.source] : []);
  const sources = Array.from(new Set(rawSources)); // Deduplicate

  return (
    <SharedOriginCell
      origins={sources}
      activeOriginFilters={activeOriginFilters}
      data-testid="source-chips"
    />
  );
};

OriginCell.displayName = 'OriginCell';
